/**
 * Institutional buyer share by Finnish maakunta (region), 2020-2025.
 * Source: vertailutyokalu.html mockup `INST_DATA` constant. Each region has
 * the overall % of institutional buyers, plus per-size cutoffs that show
 * how the share grows with property size. Used by `lookupInstInterest`
 * (backend M6 placeholder until it computes this from `properties` rows
 * directly).
 *
 * Spec: SPEC_FI_FULL.md §5.1 rule 1 + §6 (renderBuyerMix).
 */
import data from './inst-data.json';

export interface InstCutoff {
  threshold: number;
  inst_pct: number;
  n: number;
}
export interface InstRegion {
  n_total: number;
  overall_inst_pct: number;
  crossover: number | null;
  cutoffs: InstCutoff[];
  pattern: 'size_dependent' | 'private_dominant';
  effective_threshold: number | null;
}
export interface InstInfo {
  pct: number;
  sampleN: number;
  usedThreshold: number;
  threshold: number | null;
  pattern: string;
  category: 'inst_strong' | 'inst_likely' | 'borderline' | 'inst_unlikely' | 'private' | 'unknown';
  comment: string;
  regionOverall: number;
}

const INST_DATA = data as Record<string, InstRegion>;

/**
 * Optional API override cache populated by `prefetchInstInterest()`. When a
 * region+size key is present, `lookupInstInterest` returns those numbers
 * instead of the static fallback. The cache is module-scoped so repeated
 * lookups stay synchronous (the call sites are inside render loops where an
 * `await` would be invasive).
 *
 * Backend endpoint: `GET /api/v1/smt/market/inst-interest?maakunta=&size_ha=`
 * (M6, smt_market_handler.go).
 */
const apiOverrides: Map<string, InstInfo> = new Map();

function apiCacheKey(maakunta: string, sizeHa: number | null | undefined): string {
  if (sizeHa == null) return `${maakunta}|*`;
  // Round to 0.1 ha so adjacent fetches share the same cache slot.
  return `${maakunta}|${Math.round(sizeHa * 10) / 10}`;
}

/**
 * Hydrate `apiOverrides` from the backend for a single (maakunta, sizeHa)
 * pair. Safe to call multiple times — the result is cached and the function
 * never throws (network errors fall through to the static lookup).
 *
 * Returns `true` when the cache now contains data the next sync lookup will
 * see; the caller can use this to trigger a re-render after the prefetch.
 */
export async function prefetchInstInterest(
  maakunta: string,
  sizeHa: number | null | undefined,
  tFn: TranslateFn,
): Promise<boolean> {
  if (!maakunta) return false;
  const key = apiCacheKey(maakunta, sizeHa);
  if (apiOverrides.has(key)) return false;

  try {
    const { getInstInterest } = await import('../api/market.ts');
    const resp = await getInstInterest(maakunta, sizeHa ?? undefined);
    if (!resp || resp.n_total < 10) return false; // not enough data — keep static fallback
    const info = mockupShape(resp, sizeHa, tFn);
    apiOverrides.set(key, info);
    return true;
  } catch {
    return false;
  }
}

/**
 * Convert the backend's `InstInterestResponse` into the same `InstInfo` shape
 * the legacy mockup port produces, so the rendering code stays unchanged.
 */
function mockupShape(
  resp: import('../api/market.ts').InstInterestResponse,
  sizeHa: number | null | undefined,
  tFn: TranslateFn,
): InstInfo {
  const pct = resp.pct ?? resp.overall_inst_pct;
  const sampleN = resp.sample_n ?? resp.n_total;
  const usedThreshold = resp.used_threshold ?? 0;
  const threshold = resp.effective_threshold ?? null;
  const category = (resp.category ?? 'unknown') as InstInfo['category'];

  let comment: string;
  if (resp.pattern === 'private_dominant') {
    comment = tFn('inst_comment_private', { pct: resp.overall_inst_pct.toFixed(0) });
  } else if (threshold === 0) {
    comment = tFn('inst_comment_strong', { pct: pct.toFixed(0) });
  } else if (sizeHa == null || threshold == null) {
    comment = tFn('inst_comment_avg', { pct: resp.overall_inst_pct.toFixed(0) });
  } else if (sizeHa >= threshold) {
    comment = pct >= 60
      ? tFn('inst_comment_above_strong', { threshold, pct: pct.toFixed(0) })
      : tFn('inst_comment_above', { threshold, pct: pct.toFixed(0) });
  } else if (sizeHa >= threshold * 0.75) {
    comment = tFn('inst_comment_border', { threshold, pct: pct.toFixed(0) });
  } else {
    comment = tFn('inst_comment_unlikely', { pct: pct.toFixed(0), threshold });
  }
  return {
    pct,
    sampleN,
    usedThreshold,
    threshold,
    pattern: resp.pattern,
    category,
    comment,
    regionOverall: resp.overall_inst_pct,
  };
}

/**
 * Port of mockup `lookupInstInterest(maakunta, sizeHa)`. Picks the largest
 * cutoff row whose threshold ≤ sizeHa AND n ≥ 10, then classifies into one
 * of: inst_strong / inst_likely / borderline / inst_unlikely / private.
 *
 * `commentFn` produces the human-readable Finnish/English string —
 * caller passes the i18n `t()` function so keys stay localised.
 */
type TranslateFn = (key: string, vars?: Record<string, string | number>) => string;

export function lookupInstInterest(
  maakunta: string,
  sizeHa: number | null | undefined,
  tFn: TranslateFn,
): InstInfo | null {
  if (!maakunta) return null;
  // M6: prefer the API-derived value when prefetched; static JSON is the
  // last-resort fallback for regions that lack ≥10 buyer rows in production.
  const cached = apiOverrides.get(apiCacheKey(maakunta, sizeHa));
  if (cached) return cached;
  const region = INST_DATA[maakunta];
  if (!region) return null;

  let pct = region.overall_inst_pct;
  let sampleN = region.n_total;
  let usedThreshold = 0;

  if (sizeHa != null) {
    let best = region.cutoffs[0];
    for (const c of region.cutoffs) {
      if (c.threshold <= sizeHa && c.n >= 10) best = c;
    }
    pct = best.inst_pct;
    sampleN = best.n;
    usedThreshold = best.threshold;
  }

  const threshold = region.effective_threshold;
  let category: InstInfo['category'];
  let comment: string;

  if (region.pattern === 'private_dominant') {
    category = 'private';
    comment = tFn('inst_comment_private', { pct: region.overall_inst_pct.toFixed(0) });
  } else if (threshold === 0) {
    category = 'inst_strong';
    comment = tFn('inst_comment_strong', { pct: pct.toFixed(0) });
  } else if (sizeHa == null || threshold == null) {
    category = 'unknown';
    comment = tFn('inst_comment_avg', { pct: region.overall_inst_pct.toFixed(0) });
  } else if (sizeHa >= threshold) {
    if (pct >= 60) {
      category = 'inst_strong';
      comment = tFn('inst_comment_above_strong', { threshold, pct: pct.toFixed(0) });
    } else {
      category = 'inst_likely';
      comment = tFn('inst_comment_above', { threshold, pct: pct.toFixed(0) });
    }
  } else if (sizeHa >= threshold * 0.75) {
    category = 'borderline';
    comment = tFn('inst_comment_border', { threshold, pct: pct.toFixed(0) });
  } else {
    category = 'inst_unlikely';
    comment = tFn('inst_comment_unlikely', { pct: pct.toFixed(0), threshold });
  }

  return {
    pct,
    sampleN,
    usedThreshold,
    threshold,
    pattern: region.pattern,
    category,
    comment,
    regionOverall: region.overall_inst_pct,
  };
}
